import React, { useState, useEffect, useRef } from 'react';
import { FaSpinner, FaTimes } from 'react-icons/fa';
import { enqueueAndPoll } from '../utils/jobPolling';
import { backendConfig } from '../config/backendConfig';

const API_BASE = backendConfig.baseUrl || process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

const ArtmosphereFeedbackModal = ({ 
  isOpen, 
  onClose, 
  activityData, 
  userArtworkUrl, 
  onFeedbackComplete,
  activityPageUrl,
  onMarkComplete,
  onReupload
}) => {
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);
  const isRequestInProgressRef = useRef(false);

  useEffect(() => {
    // Only trigger when modal opens with all required data
    if (isOpen && activityData && userArtworkUrl && activityPageUrl) {
      // Reset state when modal opens
      setLoading(true);
      setError(null);
      setFeedback(null);
      generateFeedback();
    } else if (!isOpen) {
      // Cancel any ongoing requests when modal closes
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      // Reset state when modal closes
      setLoading(false);
      setError(null);
      setFeedback(null);
      isRequestInProgressRef.current = false;
    }

    // Cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      isRequestInProgressRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only depend on isOpen to prevent unnecessary re-renders

  const generateFeedback = async () => {
    // Prevent multiple simultaneous requests
    if (isRequestInProgressRef.current) {
      console.log('[ArtmosphereFeedback] Request already in progress, skipping...');
      return;
    }

    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    isRequestInProgressRef.current = true;
    setLoading(true);
    setError(null);
    setFeedback(null);

    try {
      const payload = {
        activityData,
        userArtworkUrl,
        activityPageUrl,
        featureType: 'daily-acts'
      };
      console.log('[ArtmosphereFeedback] Sending payload to backend:', {
        api: `${API_BASE}/api/ai-feedback/generate-feedback`,
        title: activityData?.title,
        description: activityData?.description,
        prerequisites: activityData?.prerequisites,
        steps: activityData?.steps,
        hasArtworkUrl: Boolean(userArtworkUrl),
        hasActivityPageUrl: Boolean(activityPageUrl),
        artworkUrlType: userArtworkUrl ? (userArtworkUrl.startsWith('data:') ? 'data-URL' : userArtworkUrl.startsWith('http') ? 'http-URL' : 'other') : 'none',
        activityPageUrlType: activityPageUrl ? (activityPageUrl.startsWith('data:') ? 'data-URL' : activityPageUrl.startsWith('http') ? 'http-URL' : 'other') : 'none',
        artworkUrlPreview: userArtworkUrl ? userArtworkUrl.substring(0, 100) : 'none',
        activityPageUrlPreview: activityPageUrl ? activityPageUrl.substring(0, 100) : 'none'
      });

      // COST OPTIMIZATION: Use background workers with polling to reduce HTTP handler costs
      console.log('[ArtmosphereFeedback] Enqueueing AI feedback job...');
      
      // Fix double /api/api/ issue - backendBase already includes /api for Firebase Functions
      const feedbackUrl = API_BASE.endsWith('/api') 
        ? `${API_BASE}/ai-feedback/generate-feedback`
        : `${API_BASE}/api/ai-feedback/generate-feedback`;
      
      console.log('[ArtmosphereFeedback] Using feedback URL:', feedbackUrl);
      
      const result = await enqueueAndPoll(
        feedbackUrl,
        payload,
        {
          interval: 2000, // Poll every 2 seconds
          maxAttempts: 150, // Max 5 minutes
          onProgress: (status, job, attempts) => {
            let message = '';
            if (status === 'enqueued') message = 'Preparing AI feedback...';
            else if (status === 'processing') message = `Generating feedback (attempt ${attempts})...`;
            else if (status === 'completed') message = 'Feedback ready!';
            console.log('[ArtmosphereFeedback] Job status:', status, message);
          }
        }
      );

      // Process the result - it should have the feedback structure
      console.log('[ArtmosphereFeedback] Received result from enqueueAndPoll:', result);
      console.log('[ArtmosphereFeedback] Result type:', typeof result);
      console.log('[ArtmosphereFeedback] Result keys:', result ? Object.keys(result) : 'null');
      console.log('[ArtmosphereFeedback] Result isOffTopic:', result?.isOffTopic);
      console.log('[ArtmosphereFeedback] Result has strengths:', Array.isArray(result?.strengths));
      console.log('[ArtmosphereFeedback] Result has artTechniques:', Array.isArray(result?.artTechniques));
      console.log('[ArtmosphereFeedback] Result has improvements:', Array.isArray(result?.improvements));
      console.log('[ArtmosphereFeedback] Result overallMessage:', result?.overallMessage);
      
      // Ensure result has the expected structure
      if (result && (result.isOffTopic !== undefined || result.strengths || result.improvements)) {
        setFeedback(result);
        console.log('[ArtmosphereFeedback] Feedback set successfully');
      } else {
        console.error('[ArtmosphereFeedback] Invalid result structure:', result);
        throw new Error('Invalid feedback result structure received from backend');
      }

    } catch (err) {
      console.error('Error generating feedback:', err);
      
      // Only use fallback if it's a network error or the request was aborted (user closed modal)
      // Otherwise, show the error so we can debug backend issues
      if (err.name === 'AbortError') {
        // Request was aborted (likely user closed modal) - don't show feedback
        console.log('[ArtmosphereFeedback] Request aborted, likely modal was closed');
        return; // Exit early, don't set feedback
      } else if (err.message && (err.message.includes('Failed to fetch') || err.message.includes('Network'))) {
        // Network error - use fallback
        setError(null);
        setFeedback({
          strengths: [
            "I can see you put effort into this activity!",
            "You showed creativity in your approach.",
            "Great job completing the task!"
          ],
          improvements: [
            "Try experimenting with different materials next time",
            "Consider adding more details to tell your story",
            "Don't be afraid to try new techniques"
          ],
          overallMessage: "Keep exploring your creativity! Every artist learns through practice."
        });
      } else {
        // Backend error - show error message but also provide fallback feedback
        console.error('[ArtmosphereFeedback] Backend error:', err);
        setError(`Unable to generate feedback: ${err.message}. Please try again.`);
        // Still provide fallback feedback
        setFeedback({
          strengths: [
            "I can see you put effort into this activity!",
            "You showed creativity in your approach.",
            "Great job completing the task!"
          ],
          improvements: [
            "Try experimenting with different materials next time",
            "Consider adding more details to tell your story",
            "Don't be afraid to try new techniques"
          ],
          overallMessage: "Keep exploring your creativity! Every artist learns through practice."
        });
      }
    } finally {
      setLoading(false);
      isRequestInProgressRef.current = false;
    }
  };

  const handleFeedbackComplete = async () => {
    try {
      if (onFeedbackComplete) {
        // Save the feedback data (but don't mark as complete yet)
        await onFeedbackComplete(feedback);
      }
      
      // Close the modal - user will see "Mark Complete" and "Re-upload" buttons
      onClose();
    } catch (error) {
      console.error('Error handling feedback completion:', error);
      // Still close the modal even if there's an error
      onClose();
    }
  };

  const handleMarkComplete = async () => {
    try {
      if (onFeedbackComplete) {
        // Save the feedback data first
        await onFeedbackComplete(feedback);
      }
      
      // Close the modal
      onClose();
      
      // Trigger mark complete flow (which will show share modal)
      if (onMarkComplete) {
        await onMarkComplete();
      }
    } catch (error) {
      console.error('Error handling mark complete:', error);
      onClose();
    }
  };

  const handleReupload = async () => {
    try {
      if (onFeedbackComplete) {
        // Save the feedback data first
        await onFeedbackComplete(feedback);
      }
      
      // Close the modal
      onClose();
      
      // Trigger re-upload flow
      if (onReupload) {
        await onReupload();
      }
    } catch (error) {
      console.error('Error handling re-upload:', error);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-amber-50/90 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-amber-50 rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden border border-amber-200">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-400 to-amber-300 text-amber-900 p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">Artmosphere Feedback</h2>
              <p className="text-amber-800 mt-1">Your creative journey analysis</p>
            </div>
            <button
              onClick={onClose}
              className="text-amber-900 hover:text-amber-700 transition-colors p-2"
            >
              <FaTimes size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col lg:flex-row h-[calc(90vh-112px)]">{/* subtract header height */}
          
          {/* Left Side - Only uploaded artwork, scrollable */}
          <div className="lg:w-1/2 p-6 border-r border-amber-200 overflow-y-auto">
            <div className="relative">
              {userArtworkUrl && (
                <img
                  src={userArtworkUrl}
                  alt="Your artwork"
                  className="w-full max-h-[70vh] object-contain rounded-lg border-2 border-amber-300 bg-white"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    setError('Failed to load artwork image');
                  }}
                />
              )}
            </div>
          </div>

          {/* Right Side - Feedback */}
          <div className="lg:w-1/2 p-6 overflow-y-auto">
            {loading ? (
              <div className="flex flex-col items-center justify-center min-h-[400px]">
                {/* Sophisticated Loading Animation */}
                <div className="relative mb-6">
                  {/* Outer ring */}
                  <div className="w-16 h-16 border-4 border-amber-200 rounded-full"></div>
                  {/* Animated inner ring */}
                  <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-amber-500 border-r-amber-400 rounded-full animate-spin"></div>
                  {/* Center dot */}
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-amber-500 rounded-full"></div>
                </div>
                
                {/* Elegant text with subtle animation */}
                <div className="text-center space-y-2">
                  <h3 className="text-xl font-semibold text-amber-800">This artwork looks beautiful</h3>
                  <p className="text-amber-600 text-sm max-w-xs leading-relaxed">
                    Let's see what artmosphere has to say about it
                  </p>
                </div>
                
                {/* Subtle progress indicator */}
                <div className="mt-6 w-32 h-1 bg-amber-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full animate-pulse"></div>
                </div>
              </div>
            ) : feedback ? (
              <div className="space-y-6">
                {feedback.isOffTopic ? (
                  // Off-topic feedback display
                  <>
                    {/* Gentle Guidance */}
                    <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 border-l-4 border-amber-400 shadow-lg">
                      <h3 className="text-xl font-semibold text-amber-700 mb-4">Gentle Guidance</h3>
                      <p className="text-amber-800 leading-relaxed text-lg">{feedback.gentleGuidance}</p>
                    </div>

                    {/* Suggestions */}
                    <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 border-l-4 border-amber-500 shadow-lg">
                      <h3 className="text-xl font-semibold text-amber-700 mb-4">What You Could Try Instead</h3>
                      <ul className="space-y-3">
                        {feedback.suggestions.map((suggestion, index) => (
                          <li key={index} className="text-amber-800 leading-relaxed flex items-start">
                            <span className="text-amber-500 mr-3 text-lg">•</span>
                            <span className="text-amber-800">{suggestion}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Encouragement */}
                    <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 border-l-4 border-amber-400 shadow-lg">
                      <h3 className="text-xl font-semibold text-amber-700 mb-4">Keep Going!</h3>
                      <p className="text-amber-800 leading-relaxed text-lg">{feedback.encouragement}</p>
                    </div>
                  </>
                ) : (
                  // Regular feedback display - ORDER: Strengths → Art Techniques → Improvements → Overall Message
                  <>
                    {/* Strengths - FIRST (as per prompt: "START HERE, always begin with what they did well!") */}
                    <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 border-l-4 border-amber-500 shadow-lg">
                      <h3 className="text-xl font-semibold text-amber-700 mb-4">What You Did Great!</h3>
                      <ul className="space-y-3">
                        {feedback.strengths && feedback.strengths.length > 0 ? (
                          feedback.strengths.map((strength, index) => (
                            <li key={index} className="text-amber-800 leading-relaxed flex items-start">
                              <span className="text-amber-500 mr-3 text-lg">•</span>
                              <span className="text-amber-800">{strength}</span>
                            </li>
                          ))
                        ) : (
                          <li className="text-amber-800">You showed great creativity in this activity!</li>
                        )}
                      </ul>
                    </div>

                    {/* Art Techniques Applied - SECOND (educational explanation) */}
                    {feedback.artTechniques && feedback.artTechniques.length > 0 && (
                      <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 border-l-4 border-blue-500 shadow-lg">
                        <h3 className="text-xl font-semibold text-blue-700 mb-4">Art Techniques You Applied! 🎨</h3>
                        <p className="text-blue-600 text-sm mb-3 italic">Great job using these art skills!</p>
                        <ul className="space-y-3">
                          {feedback.artTechniques.map((technique, index) => (
                            <li key={index} className="text-blue-800 leading-relaxed flex items-start">
                              <span className="text-blue-500 mr-3 text-lg">✓</span>
                              <span className="text-blue-800">{technique}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Improvements - THIRD (based on how they could attempt the activity better) */}
                    <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 border-l-4 border-amber-500 shadow-lg">
                      <h3 className="text-xl font-semibold text-amber-700 mb-4">How You Could Try This Activity Better Next Time</h3>
                      <ul className="space-y-3">
                        {feedback.improvements && feedback.improvements.length > 0 ? (
                          feedback.improvements.map((improvement, index) => (
                            <li key={index} className="text-amber-800 leading-relaxed flex items-start">
                              <span className="text-amber-500 mr-3 text-lg">•</span>
                              <span className="text-amber-800">{improvement}</span>
                            </li>
                          ))
                        ) : (
                          <li className="text-amber-800">Try experimenting with different approaches next time</li>
                        )}
                      </ul>
                    </div>

                    {/* Overall Message - LAST (encouraging summary) */}
                    {feedback.overallMessage && (
                      <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 border-l-4 border-amber-400 shadow-lg">
                        <h3 className="text-xl font-semibold text-amber-700 mb-4">Overall Message</h3>
                        <p className="text-amber-800 leading-relaxed text-lg">{feedback.overallMessage}</p>
                      </div>
                    )}
                  </>
                )}

                {/* Action Buttons - Show Mark Complete and Re-upload after feedback is generated */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <button
                    onClick={handleMarkComplete}
                    className="flex-1 bg-gradient-to-r from-amber-500 to-amber-400 text-white py-3 px-6 rounded-lg hover:from-amber-600 hover:to-amber-500 transition-all duration-300 font-medium"
                  >
                    Mark Complete
                  </button>
                  <button
                    onClick={handleReupload}
                    className="flex-1 bg-white border-2 border-amber-500 text-amber-700 py-3 px-6 rounded-lg hover:bg-amber-50 transition-all duration-300 font-medium"
                  >
                    Re-upload based on feedback
                  </button>
                  <button
                    onClick={onClose}
                    className="px-6 py-3 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 transition-colors font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center h-64">
                <div className="text-red-500 text-4xl mb-4">⚠️</div>
                <p className="text-red-700 text-lg font-medium mb-2">Oops! Something went wrong</p>
                <p className="text-red-600 text-sm text-center mb-4">{error}</p>
                <button
                  onClick={generateFeedback}
                  className="bg-amber-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600 transition-colors"
                >
                  Try Again
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtmosphereFeedbackModal;
